import {useMemo} from "react";
import {MessageEntity, messagesSelector, useGetAllMessagesByObjectQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {Box, Modal, ModalProps} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {CommentList} from "../CommentList/CommentList";

export type EntityCommentListProps = {
  objectId: string
  tenantCode?: string
  onAddComment?: () => void
  onEdit?: (message: MessageEntity) => void
  onDelete?: (message: MessageEntity) => void
  addCommentLabel?: string
  showEmpty?: boolean
  emptyMessage?: string
}

export const EntityCommentList = (props: EntityCommentListProps) => {
  const {objectId, tenantCode, onAddComment, onEdit, onDelete, addCommentLabel, emptyMessage, showEmpty = true} = props

  const {t} = useTranslation(["common"]);

  const selectMessages = useMemo(() => {
    return messagesSelector()
  }, []);

  const queryArg = useMemo(() => {
    if (objectId) {
      return {
        objectId: objectId,
        tenantCode: tenantCode
      }
    }

    return skipToken
  }, [objectId, tenantCode])

  const {messages, isLoading} = useGetAllMessagesByObjectQuery(queryArg, {
    selectFromResult: (result) => ({
      messages: selectMessages(result),
      isLoading: result.isLoading,
    })
  });

  return (
    <CommentList
      comments={messages}
      isLoading={isLoading}
      empty={{
        show: showEmpty,
        message: emptyMessage ?? t<string>("common.entity.message.list.empty", "No Comments"),
      }}
      onAddComment={onAddComment}
      onEdit={onEdit}
      onDelete={onDelete}
      addCommentLabel={addCommentLabel}/>
  )
}

export interface EntityCommentListModalProps extends ModalProps {
  listProps: EntityCommentListProps
}

export const EntityCommentListModal = (props: EntityCommentListModalProps) => {
  const {t} = useTranslation(["common"])

  const {
    title = t<string>("common.modal.entitycommentlist.title", "Comments"),
    listProps,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <EntityCommentList {...listProps} />
      </Box>
    </Modal>
  );
};
